import useSidebarState from '../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Doughnut, PolarArea } from 'react-chartjs-2';
import Sidebar from './Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, update } from 'firebase/database';
import { database } from '../firebase';
import CVUploadManager from './applicants/CVUploadManager';
import ImportedApplicantDetailsPopup from './applicants/ImportedApplicantDetailsPopup';
import FeatureGate from './Trial/FeatureGate';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { syncCommunityMember, triggerCommunityAutoSync } from '../services/communityService';
import {
  CHART_COLORS,
  MAX_COMPARE_CANDIDATES,
  extractTalentPoolFromProfile,
  loadImportedTalentPoolProfiles,
  loadTalentPoolSummary,
  queueTalentPoolProfilesForParsing,
  syncTalentPoolParsingBatches,
  toTimestamp
} from '../services/importedTalentPoolService';
import {
  canonicalizeAdjacentRoles,
  canonicalizeDepartment,
  canonicalizeIndustries,
  canonicalizeLeadershipReadiness,
  canonicalizePositionFamily,
  canonicalizeRoleList,
  canonicalizeRoleTitle
} from '../utils/talentPoolTaxonomy';
import './ImportedCVs.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const AVATAR_HUES = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
const PARSING_LIVE_STATUSES = new Set(['queued', 'parsing', 'batch_pending', 'batch_submitted']);
const PARSING_ISSUE_STATUSES = new Set(['failed', 'blocked']);
const SENIORITY_ORDER = {
  intern: 0,
  junior: 1,
  associate: 2,
  mid: 3,
  senior: 4,
  lead: 5,
  staff: 6,
  principal: 7,
  head: 8,
  director: 9,
  vp: 10,
  executive: 11
};

const dedupe = (values) => [...new Set((Array.isArray(values) ? values : []).filter(Boolean))];
const LANGUAGE_PROFICIENCY_PATTERN = /\b(native|mother tongue|bilingual|fluent|professional|conversational|basic|intermediate|advanced|proficient|c2|c1|b2|b1|a2|a1)\b/gi;
const LANGUAGE_ALIASES = {
  arabic: 'Arabic',
  english: 'English',
  french: 'French',
  spanish: 'Spanish',
  german: 'German',
  italian: 'Italian',
  portuguese: 'Portuguese',
  chinese: 'Chinese',
  mandarin: 'Mandarin',
  cantonese: 'Cantonese',
  hindi: 'Hindi',
  urdu: 'Urdu',
  turkish: 'Turkish',
  russian: 'Russian'
};
const SKILL_ALIASES = {
  react: 'React',
  'react js': 'React',
  reactjs: 'React',
  'react javascript': 'React',
  javascript: 'JavaScript',
  js: 'JavaScript',
  typescript: 'TypeScript',
  ts: 'TypeScript',
  node: 'Node.js',
  'node js': 'Node.js',
  nodejs: 'Node.js',
  'next js': 'Next.js',
  nextjs: 'Next.js',
  'vue js': 'Vue.js',
  vuejs: 'Vue.js',
  'angular js': 'Angular',
  angularjs: 'Angular',
  postgresql: 'PostgreSQL',
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  mongodb: 'MongoDB',
  aws: 'AWS',
  gcp: 'GCP',
  azure: 'Azure',
  docker: 'Docker',
  kubernetes: 'Kubernetes',
  figma: 'Figma',
  photoshop: 'Photoshop',
  illustrator: 'Illustrator'
};

const normalizeText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const toTitleCase = (value) => String(value || '')
  .split(' ')
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ');

const normalizeLanguageLabel = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return '';
  return LANGUAGE_ALIASES[normalized] || toTitleCase(normalized);
};

const normalizeLanguageValues = (values) => dedupe(
  (Array.isArray(values) ? values : []).flatMap((value) => String(value || '')
    .split(/\s*(?:,|\/|;|\||&|\sand\s)\s*/i)
    .map((item) => item
      .replace(/\([^)]*\)/g, ' ')
      .replace(LANGUAGE_PROFICIENCY_PATTERN, ' ')
      .replace(/\s+/g, ' ')
      .trim())
    .map(normalizeLanguageLabel)
    .filter(Boolean))
);

const normalizeSkillLabel = (value) => {
  const normalized = normalizeText(value)
    .replace(/\b(advanced|intermediate|beginner|basic|expert|experienced|proficient)\b/g, ' ')
    .replace(/\b\d+\+?\s*(years|yrs|year)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';
  return SKILL_ALIASES[normalized] || toTitleCase(normalized);
};

const normalizeSkillValues = (values) => dedupe(
  (Array.isArray(values) ? values : []).flatMap((value) => String(value || '')
    .split(/\s*(?:,|\/|;|\||&|\sand\s)\s*/i)
    .map((item) => item.replace(/\([^)]*\)/g, ' ').trim())
    .map(normalizeSkillLabel)
    .filter(Boolean))
);

const intersectNormalizedLabels = (leftValues, rightValues, limit = 8) => {
  const rightKeys = new Set((rightValues || []).map((value) => normalizeText(value)).filter(Boolean));
  return dedupe((leftValues || []).filter((value) => rightKeys.has(normalizeText(value)))).slice(0, limit);
};

const formatFieldLabel = (path) => {
  const raw = String(path || '').split('.').pop() || path || '';
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const getSeniorityRank = (value) => {
  const normalized = normalizeText(value);
  const found = Object.entries(SENIORITY_ORDER).find(([key]) => normalized.includes(key));
  return found ? found[1] : -1;
};

const getAvatarHue = (name) => {
  let hash = 0;
  const value = name || '';
  for (let index = 0; index < value.length; index += 1) {
    hash = value.charCodeAt(index) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % AVATAR_HUES;
};

const toDistributionRows = (values, limit = 8) => {
  const counts = values.reduce((accumulator, value) => {
    const key = String(value || '').trim();
    if (!key) return accumulator;
    accumulator[key] = (accumulator[key] || 0) + 1;
    return accumulator;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, value]) => ({ label, value }));
};

const profileAssignedToJob = (profile, job) => {
  if (!profile || !job) return false;

  const jobTitle = normalizeText(job.title);
  const jobId = String(job.id || '');
  const hasMatchingConnection = (profile.activeConnections || []).some((connection) => (
    String(connection?.jobId || '') === jobId
    || normalizeText(connection?.jobAppliedFor) === jobTitle
  ));

  if (hasMatchingConnection) return true;

  return (profile.currentJobs || []).some((currentJob) => normalizeText(currentJob) === jobTitle);
};

const profileHasJobAssignment = (profile) => (
  (profile.activeConnectionsCount || 0) > 0
  || (profile.activeConnections || []).length > 0
  || (profile.currentJobs || []).length > 0
);

const buildCandidateSynopsis = (profile) => {
  if (profile.recruiterSummary) return profile.recruiterSummary;

  const role = profile.primaryPosition || profile.positionFamily || 'Role profile missing';
  const seniority = profile.primarySeniority || profile.seniorityBucket || 'Seniority missing';
  const years = Number(profile.totalYearsExperience);
  const yearsLabel = Number.isFinite(years) && years > 0 ? `${years} years of experience` : 'experience depth not yet extracted';
  const industry = profile.industryTags[0] || profile.industries[0];
  const location = profile.location || '';

  return [
    `${role} profile at ${seniority} level`,
    yearsLabel,
    industry ? `with background in ${industry}` : null,
    location ? `based in ${location}` : null
  ].filter(Boolean).join(', ');
};

const getCriticalGapLabels = (profile) => {
  const hasDirectContact = profile.hasEmail || profile.hasPhone;
  const uiRelevantField = (field) => {
    if (field === 'identity.email' || field === 'identity.phone') {
      return !hasDirectContact;
    }
    return !['fit.roleCoverage'].includes(field);
  };
  const fieldLabels = [
    ...(Array.isArray(profile.missingRequiredFields) ? profile.missingRequiredFields.filter(uiRelevantField).map(formatFieldLabel) : []),
    ...(Array.isArray(profile.lowConfidenceFields) ? profile.lowConfidenceFields.filter(uiRelevantField).map((field) => `${formatFieldLabel(field)} confidence`) : [])
  ];

  const signalLabels = [];
  if (!profile.hasCV) signalLabels.push('CV missing');
  if (!hasDirectContact) signalLabels.push('Direct contact missing');
  if (!profile.primaryPosition) signalLabels.push('Primary role missing');
  if (!profile.primarySeniority) signalLabels.push('Seniority missing');
  if (!(Number.isFinite(Number(profile.totalYearsExperience)) && Number(profile.totalYearsExperience) > 0)) signalLabels.push('Experience missing');
  if (!profile.normalizedSkills.length) signalLabels.push('Skills missing');

  return dedupe([...fieldLabels, ...signalLabels]).slice(0, 4);
};

const getDataStatus = (profile) => {
  if (PARSING_ISSUE_STATUSES.has(profile.parsingStatus)) {
    return { key: 'needs-enrichment', label: 'Needs CV enrichment', icon: 'fa-circle-exclamation' };
  }
  if (profile.needsEnrichment) {
    return { key: 'needs-enrichment', label: 'Missing key details', icon: 'fa-circle-exclamation' };
  }
  return null;
};

const getProfileSourceLabel = (profile, t) => {
  const source = normalizeText(profile.source);
  const sourceJob = profile.currentJobs[0] || '';

  if (source.includes('rejected')) {
    return sourceJob
      ? t('imported.sourceRejectedFromJob', {
        job: sourceJob,
        defaultValue: `Rejected from ${sourceJob}`
      })
      : t('imported.sourceRejected', 'Rejected ATS candidate');
  }

  if (
    source.includes('imported')
    || source.includes('cv upload')
    || source.includes('bulk upload')
    || source.includes('profile derivation')
  ) {
    return t('imported.sourceImported', 'Imported CV');
  }

  return t('imported.sourceUnknown', 'Imported source');
};

const normalizeTalentProfile = (profile) => {
  const base = profile && typeof profile === 'object' ? profile : {};
  const extraction = base.extraction && typeof base.extraction === 'object' ? base.extraction : {};
  const analytics = base.analytics && typeof base.analytics === 'object' ? base.analytics : {};
  const processing = base.processing && typeof base.processing === 'object' ? base.processing : {};
  const parsedData = base.parsedData && typeof base.parsedData === 'object' ? base.parsedData : {};
  const identity = extraction.identity && typeof extraction.identity === 'object' ? extraction.identity : {};
  const experience = extraction.experience && typeof extraction.experience === 'object' ? extraction.experience : {};
  const skills = extraction.skills && typeof extraction.skills === 'object' ? extraction.skills : {};
  const signalSet = extraction.signals && typeof extraction.signals === 'object' ? extraction.signals : {};
  const fit = extraction.fit && typeof extraction.fit === 'object'
    ? extraction.fit
    : (extraction.fitInference && typeof extraction.fitInference === 'object' ? extraction.fitInference : {});
  const marketProfile = extraction.marketProfile && typeof extraction.marketProfile === 'object' ? extraction.marketProfile : {};
  const hrInsights = extraction.hrInsights && typeof extraction.hrInsights === 'object' ? extraction.hrInsights : {};
  const headline = extraction.professionalHeadline && typeof extraction.professionalHeadline === 'object' ? extraction.professionalHeadline : {};

  const rawPrimaryPosition = base.primaryPosition || experience.primaryPosition || experience.currentTitle || headline.currentTitle || parsedData.currentTitle || '';
  const rawIndustries = dedupe([
    ...(analytics.industryTags || []),
    ...(marketProfile.industries || []),
    ...(parsedData.industries || [])
  ]);
  const industries = canonicalizeIndustries(rawIndustries);
  const positionFamily = base.positionFamily
    || analytics.positionFamily
    || canonicalizePositionFamily(marketProfile.positionFamily || rawPrimaryPosition, industries);
  const primaryPosition = canonicalizeRoleTitle(rawPrimaryPosition, { positionFamily });
  const department = base.department
    || analytics.department
    || marketProfile.department
    || parsedData.department
    || canonicalizeDepartment(marketProfile.primaryFunction, {
      positionFamily,
      title: primaryPosition,
      skills: skills.normalized || parsedData.skills || []
    });
  const primarySeniority = base.primarySeniority || experience.seniority || parsedData.seniority || '';
  const seniorityBucket = base.seniorityBucket || analytics.seniorityBucket || marketProfile.seniorityBucket || primarySeniority;

  const location = base.location
    || identity.location
    || parsedData.location
    || (Array.isArray(marketProfile.geographies) ? marketProfile.geographies[0] : '')
    || '';
  const totalYearsExperience = Number(base.totalYearsExperience || experience.totalYears || parsedData.totalYearsExperience || 0);
  const currentJobs = dedupe(base.currentJobs || base.assigned_jobs || []);
  const normalizedSkills = normalizeSkillValues(skills.normalized || parsedData.skills || []);
  const languages = normalizeLanguageValues(skills.languages || parsedData.languages || marketProfile.languageTags || []);
  const certifications = dedupe(skills.certifications || parsedData.certifications || []);
  const methodologies = dedupe(skills.methodologies || []);
  const tools = dedupe(skills.tools || []);
  const educationItems = Array.isArray(extraction.education)
    ? extraction.education
    : (Array.isArray(parsedData.education) ? parsedData.education : []);
  const experienceTimeline = Array.isArray(experience.timeline)
    ? experience.timeline
    : (Array.isArray(parsedData.experience) ? parsedData.experience : []);
  const domainExposure = canonicalizeIndustries([
    ...(Array.isArray(experience.domainExposure) ? experience.domainExposure : []),
    ...(Array.isArray(parsedData.industries) ? parsedData.industries : []),
    ...(Array.isArray(marketProfile.industries) ? marketProfile.industries : [])
  ]);
  const geographies = dedupe(marketProfile.geographies || []);
  const currentCompany = experience.currentCompany || '';
  const currentTitle = canonicalizeRoleTitle(
    experience.currentTitle || headline.currentTitle || rawPrimaryPosition,
    { positionFamily }
  );
  const headlineSummary = headline.headlineSummary || '';
  const peopleManagementScope = experience.peopleManagementScope || '';
  const leadershipScope = experience.leadershipScope || '';
  const budgetOwnership = experience.budgetOwnership || '';
  const promotionPattern = experience.promotionPattern || signalSet.promotionPattern || '';
  const stabilitySignal = experience.stabilitySignal || signalSet.careerStability || '';
  const tenurePattern = signalSet.tenurePattern || '';
  const geographicMobility = signalSet.geographicMobility || '';
  const workModePreference = signalSet.workModePreference || '';
  const specialistVsGeneralist = signalSet.specialistVsGeneralist || '';
  const employmentGaps = dedupe(experience.employmentGaps || []);
  const recommendedNextActions = dedupe(hrInsights.recommendedNextActions || []);
  const missingContactData = dedupe(hrInsights.missingContactData || []);
  const missingEvaluationData = dedupe(hrInsights.missingEvaluationData || []);
  const parsingStatus = base.parsingStatus
    || processing.status
    || ((base.extraction || base.analytics || primaryPosition) ? 'parsed' : (base.hasCV || base.cvLink ? 'idle' : 'blocked'));
  const recruiterSummary = base.recruiterSummary || analytics.recruiterSummary || hrInsights.recruiterSummary || '';
  const topRoleArchetypes = canonicalizeRoleList(
    base.topRoleArchetypes || analytics.topRoleArchetypes || fit.roleArchetypes || [],
    { positionFamily, fallback: [primaryPosition] }
  );
  const adjacentRoles = canonicalizeAdjacentRoles(fit.adjacentRoles || analytics.adjacentRoles || [], {
    positionFamily,
    primaryRole: primaryPosition
  });
  const leadershipReadiness = canonicalizeLeadershipReadiness(
    fit.leadershipReadiness || analytics.leadershipReadiness || '',
    {
      seniorityBucket,
      title: primaryPosition,
      years: totalYearsExperience,
      peopleManagementScope: experience.peopleManagementScope || '',
      leadershipScope: experience.leadershipScope || ''
    }
  );
  const criticalGapLabels = getCriticalGapLabels({
    ...base,
    hasCV: Boolean(base.hasCV || base.cvLink),
    hasEmail: Boolean(base.email || identity.email || (identity.emails || [])[0]),
    hasPhone: Boolean(base.phone || identity.phone || (identity.phones || [])[0]),
    primaryPosition,
    primarySeniority,
    normalizedSkills,
    missingRequiredFields: analytics.missingRequiredFields || base.missingRequiredFields || [],
    lowConfidenceFields: analytics.lowConfidenceFields || base.lowConfidenceFields || []
  });

  const needsEnrichment = PARSING_ISSUE_STATUSES.has(parsingStatus)
    || criticalGapLabels.length > 0;

  return {
    ...base,
    extraction,
    analytics,
    processing,
    parsedData,
    identity,
    experience,
    skills,
    fit,
    marketProfile,
    hrInsights,
    headline,
    applicantId: base.applicantId || '',
    name: base.name || identity.fullName || 'Unknown',
    email: base.email || identity.email || (identity.emails || [])[0] || '',
    phone: base.phone || identity.phone || (identity.phones || [])[0] || '',
    currentJobs,
    hasCV: Boolean(base.hasCV || base.cvLink),
    hasEmail: Boolean(base.email || identity.email || (identity.emails || [])[0]),
    hasPhone: Boolean(base.phone || identity.phone || (identity.phones || [])[0]),
    location,
    primaryPosition,
    primarySeniority,
    totalYearsExperience: Number.isFinite(totalYearsExperience) ? totalYearsExperience : 0,
    normalizedSkills,
    languages,
    certifications,
    methodologies,
    tools,
    educationItems,
    experienceTimeline,
    domainExposure,
    geographies,
    industries,
    department,
    industryTags: canonicalizeIndustries(analytics.industryTags || industries),
    marketComparisonTags: dedupe(analytics.marketComparisonTags || []),
    targetRoles: dedupe(headline.targetRoles || parsedData.targetRoles || []),
    workAuthorization: identity.workAuthorization || parsedData.workAuthorization || '',
    currentCompany,
    currentTitle,
    headlineSummary,
    peopleManagementScope,
    leadershipScope,
    budgetOwnership,
    promotionPattern,
    stabilitySignal,
    tenurePattern,
    geographicMobility,
    workModePreference,
    specialistVsGeneralist,
    employmentGaps,
    recommendedNextActions,
    missingContactData,
    missingEvaluationData,
    positionFamily,
    seniorityBucket,
    parsingStatus,
    parsingModel: base.parsingModel || processing.model || '',
    lastParsedAt: toTimestamp(base.lastParsedAt || processing.completedAt),
    estimatedCost: Number(base.estimatedCost || processing.estimatedCost || 0),
    tokenUsage: base.tokenUsage || processing.tokenUsage || null,
    recruiterSummary,
    topRoleArchetypes,
    adjacentRoles,
    likelyMissingRequirements: dedupe(fit.likelyMissingRequirements || []),
    strongestDifferentiators: dedupe(hrInsights.strongestDifferentiators || []),
    leadershipReadiness,
    profileShape: fit.profileShape || '',
    criticalGapLabels,
    needsEnrichment,
    summary: recruiterSummary || headlineSummary || buildCandidateSynopsis({
      ...base,
      recruiterSummary,
      primaryPosition,
      positionFamily,
      primarySeniority,
      seniorityBucket,
      totalYearsExperience,
      industryTags: analytics.industryTags || industries,
      industries,
      location
    })
  };
};

const hydrateBackendProfile = (profile) => normalizeTalentProfile(profile);

const shouldQueueBackgroundParsing = (profile) => {
  if (!profile?.hasCV || PARSING_LIVE_STATUSES.has(profile.parsingStatus)) {
    return false;
  }

  return profile.needsEnrichment || !profile.lastParsedAt;
};

const shouldEscalateCandidateParsing = (profile) => {
  if (!profile?.hasCV || PARSING_LIVE_STATUSES.has(profile.parsingStatus)) {
    return false;
  }

  return profile.needsEnrichment || profile.parsingModel === 'gpt-4.1-mini';
};

const formatNumber = (value) => `${Number(value || 0)}`;

const SummaryStrip = ({ stats }) => (
  <div className="cv-stats-strip">
    {stats.map((item) => (
      <div key={item.key} className="cv-summary-card">
        <div className="cv-summary-icon" style={{ backgroundColor: item.color }}>
          <i className={`fa-solid ${item.icon} fa-fw`} />
        </div>
        <div className="cv-summary-copy">
          <span className="cv-summary-value">{formatNumber(item.value)}</span>
          <span className="cv-summary-label">{uiText(item.label)}</span>
        </div>
      </div>
    ))}
  </div>
);

const CandidateCard = ({
  profile,
  t,
  openAddToJobModal,
  downloadCV,
  setSelectedApplicant,
  handleDeleteProfile,
  deletingProfileId
}) => {
  useUiLanguage();
  const hue = getAvatarHue(profile.name);
  const initial = (profile.name || 'U').charAt(0).toUpperCase();
  const skillChips = profile.normalizedSkills.slice(0, 4);
  const jobStatus = profile.currentJobs.length > 0
    ? profile.currentJobs[0]
    : t('imported.unassigned', 'Unassigned');
  const roleLabel = profile.primaryPosition || profile.currentTitle || profile.positionFamily || t('imported.roleMissing', 'Role missing');
  const sourceLabel = getProfileSourceLabel(profile, t);
  const dataStatus = getDataStatus(profile);
  const isDeleting = deletingProfileId === profile.applicantId;

  return (
    <div className="cv-card" onClick={() => setSelectedApplicant(profile)}>
      <div className="cv-card-header">
        <div className={`cv-avatar hue-${hue}`}>{initial}</div>
        <div className="cv-card-heading">
          <div className="cv-card-name-row">
            <span className="cv-card-name">{profile.name}</span>
            <span className="cv-card-role">{roleLabel}</span>
            <span className={`cv-card-location ${profile.location ? '' : 'muted'}`}>
              <i className="fa-solid fa-location-dot" />
              {profile.location || t('imported.notSpecified', 'Not specified')}
            </span>
          </div>
        </div>
      </div>

      <div className="cv-card-stats">
        <div className="cv-stat-item">
          <i className="fa-solid fa-chart-simple" />
          <div className="cv-stat-content">
            <span className="cv-stat-value">{profile.primarySeniority || '—'}</span>
            <span className="cv-stat-label">{uiText("Seniority")}</span>
          </div>
        </div>
        <div className="cv-stat-item">
          <i className="fa-solid fa-clock" />
          <div className="cv-stat-content">
            <span className="cv-stat-value">
              {Number(profile.totalYearsExperience) > 0 ? `${profile.totalYearsExperience}y` : '—'}
            </span>
            <span className="cv-stat-label">{uiText("Experience")}</span>
          </div>
        </div>
      </div>

      {skillChips.length > 0 && (
        <div className="cv-card-skills">
          <div className="cv-skills-label">
            <i className="fa-solid fa-wand-magic-sparkles" />
            <span>{uiText("Key Skills")}</span>
          </div>
          <div className="cv-skill-row">
            {skillChips.map((item) => (
              <span key={item} className="cv-skill-chip">{item}</span>
            ))}
          </div>
        </div>
      )}

      <div className="cv-card-context">
        <div className="cv-context-item">
          <i className="fa-solid fa-building" />
          <span className="cv-context-label">{uiText("Current:")}</span>
          <span className={`cv-context-value ${profile.currentCompany ? '' : 'muted'}`}>
            {profile.currentCompany || uiText("Not specified")}
          </span>
        </div>
        <div className="cv-context-item">
          <i className="fa-solid fa-folder-tree" />
          <span className="cv-context-label">{uiText("Source:")}</span>
          <span className="cv-context-value">{sourceLabel}</span>
        </div>
        <div className="cv-context-item">
          <i className="fa-solid fa-briefcase" />
          <span className="cv-context-label">{uiText("Assigned to:")}</span>
          <span className={`cv-context-value ${profile.currentJobs.length > 0 ? 'highlight' : 'muted'}`}>
            {jobStatus}
          </span>
        </div>
      </div>

      {dataStatus && (
        <div className="cv-card-alert">
          <i className={`fa-solid ${dataStatus.icon}`} />
          <span>{uiText(dataStatus.label)}</span>
        </div>
      )}

      <div className="cv-card-footer">
        <div className="cv-card-actions" onClick={(event) => event.stopPropagation()}>
          <button
            className="cv-action-btn"
            title={t('imported.download')}
            onClick={(event) => {
              event.stopPropagation();
              downloadCV(profile);
            }}
            disabled={!profile.cvLink || isDeleting}
          >
            <i className="fa-solid fa-download" />
          </button>

          <button
            className="cv-action-btn danger"
            title={t('imported.delete', 'Delete')}
            onClick={(event) => handleDeleteProfile(profile, event)}
            disabled={isDeleting}
          >
            {isDeleting
              ? <i className="fa-solid fa-spinner fa-spin" />
              : <i className="fa-solid fa-trash" />}
          </button>

          <button className="cv-add-job-btn" onClick={(event) => openAddToJobModal(profile, event)} disabled={isDeleting}>
            <i className="fa-solid fa-plus" />
            <span>{uiText("Add to Job")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Panel = ({ title, subtitle, actions = null, children, wide = false, variant = 'default' }) => (
  <section className={`cv-panel${wide ? ' wide' : ''}${variant === 'compare' ? ' compare-surface' : ''}`}>
    <div className="cv-panel-head">
      <div>
        <h3>{title}</h3>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

const CompareHeroCard = ({ profile, t }) => {
  if (!profile) {
    return (
      <div className="cv-compare-hero-card empty">
        <strong>{t('imported.compareSlotOpen', 'Open slot')}</strong>
        <span>{t('imported.compareSlotHint', 'Choose another candidate above')}</span>
      </div>
    );
  }

  const topFacts = [
    { label: t('imported.experience', 'Experience'), value: profile.totalYearsExperience ? `${profile.totalYearsExperience}y` : '—' },
    { label: t('imported.seniority', 'Seniority'), value: profile.primarySeniority || profile.seniorityBucket || '—' },
    { label: t('imported.currentCompany', 'Current company'), value: profile.currentCompany || '—' },
    { label: t('imported.domainExposure', 'Domain exposure'), value: profile.domainExposure[0] || profile.industryTags[0] || '—' },
    { label: t('imported.credentials', 'Credentials'), value: profile.certifications.length ? `${profile.certifications.length}` : '—' },
    { label: t('imported.directContact', 'Direct contact'), value: (profile.hasEmail || profile.hasPhone) ? t('imported.contactAvailable', 'Available') : t('imported.contactMissing', 'Missing') }
  ];

  return (
    <div className="cv-compare-hero-card">
      <div className="cv-compare-hero-top">
        <div>
          <strong>{profile.name}</strong>
          <span>{[profile.primaryPosition || profile.positionFamily || t('imported.roleMissing', 'Role missing'), profile.primarySeniority || profile.seniorityBucket].filter(Boolean).join(' • ')}</span>
        </div>
        <div className="cv-compare-badge">{profile.location || t('imported.notAvailable', 'Not available')}</div>
      </div>

      <div className="cv-compare-facts">
        {topFacts.map((item) => (
          <div key={item.label} className="cv-compare-fact">
            <span>{uiText(item.label)}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const CompareListBlock = ({ title, items, empty, tone = 'default' }) => (
  <div className={`cv-compare-list ${tone}`}>
    <h4>{title}</h4>
    {items.length ? (
      <div className="cv-compare-tag-row">
        {items.map((item) => (
          <span key={item} className={`cv-compare-tag ${tone === 'warning' ? 'warning' : ''}`}>{item}</span>
        ))}
      </div>
    ) : (
      <div className="cv-empty-mini">{empty}</div>
    )}
  </div>
);

const CompareMetricDuel = ({ label, leftValue, rightValue, leftName, rightName }) => {
  const leftScore = Number(leftValue || 0);
  const rightScore = Number(rightValue || 0);
  const total = Math.max(leftScore + rightScore, 1);
  const leftWidth = `${Math.max(18, Math.round((leftScore / total) * 100))}%`;
  const rightWidth = `${Math.max(18, Math.round((rightScore / total) * 100))}%`;

  return (
    <div className="cv-compare-duel">
      <div className="cv-compare-duel-head">
        <span>{uiText(label)}</span>
        <strong>{leftScore} / {rightScore}</strong>
      </div>
      <div className="cv-compare-duel-values">
        <div>
          <strong>{leftScore}</strong>
          <span>{leftName}</span>
        </div>
        <div>
          <strong>{rightScore}</strong>
          <span>{rightName}</span>
        </div>
      </div>
      <div className="cv-compare-duel-track">
        <div className="left" style={{ width: leftWidth }} />
        <div className="right" style={{ width: rightWidth }} />
      </div>
    </div>
  );
};

const CompareSectionHeader = ({ title, subtitle }) => (
  <div className="cv-compare-section-header">
    <h4>{title}</h4>
    {subtitle ? <p>{subtitle}</p> : null}
  </div>
);

const CompareBlock = ({ children }) => (
  <section className="cv-compare-block">
    {children}
  </section>
);

const formatCompareInlineList = (items, empty) => (items.length ? items.join(', ') : empty);

const CompareCandidateLens = ({ profile, title, t }) => {
  if (!profile) return null;

  const roleSignals = dedupe([
    ...(profile.topRoleArchetypes || []).slice(0, 3),
    ...(profile.adjacentRoles || []).slice(0, 3)
  ]).filter(Boolean);
  const credentialSignals = dedupe([
    ...(profile.certifications || []).slice(0, 2),
    ...(profile.methodologies || []).slice(0, 2)
  ]).filter(Boolean);
  const followUpSignal = profile.recommendedNextActions[0]
    || profile.missingEvaluationData[0]
    || profile.missingContactData[0]
    || '';
  const signalRows = [
    {
      label: t('imported.currentCompany', 'Current company'),
      value: profile.currentCompany || t('imported.notAvailable', 'Not available')
    },
    {
      label: t('imported.targetRoles', 'Target roles'),
      value: formatCompareInlineList(
        (profile.targetRoles || []).slice(0, 3),
        t('imported.noTargetRoles', 'No target roles are mapped yet.')
      )
    },
    {
      label: t('imported.roleSignals', 'Role signals'),
      value: formatCompareInlineList(
        roleSignals,
        t('imported.noRoleSignals', 'No standardized role signals are available yet.')
      )
    },
    {
      label: t('imported.domainExposure', 'Domain exposure'),
      value: formatCompareInlineList(
        (profile.domainExposure || profile.industryTags || []).slice(0, 3),
        t('imported.noIndustrySignals', 'No domain mapping is available yet.')
      )
    },
    {
      label: t('imported.managementScope', 'Management scope'),
      value: profile.peopleManagementScope || profile.leadershipScope || t('imported.notAvailable', 'Not available')
    },
    {
      label: t('imported.careerTrajectory', 'Career trajectory'),
      value: profile.promotionPattern || profile.stabilitySignal || profile.tenurePattern || t('imported.notAvailable', 'Not available')
    },
    {
      label: t('imported.credentials', 'Credentials'),
      value: formatCompareInlineList(
        credentialSignals,
        t('imported.noCredentialSignals', 'No certifications or methodology signals are available yet.')
      )
    },
    {
      label: t('imported.leadershipReadiness', 'Leadership readiness'),
      value: profile.leadershipReadiness || t('imported.notAvailable', 'Not available')
    },
    {
      label: t('imported.workAuthorization', 'Work authorization'),
      value: profile.workAuthorization || t('imported.notAvailable', 'Not available')
    }
  ];

  return (
    <div className="cv-compare-candidate-lens">
      <div className="cv-compare-candidate-lens-head">
        <h4>{title}</h4>
        <span>{profile.primaryPosition || profile.positionFamily || t('imported.roleMissing', 'Role missing')}</span>
      </div>

      <div className="cv-compare-summary-callout">
        <span className="cv-compare-card-label">{t('imported.recruiterRead', 'Recruiter read')}</span>
        <p>{profile.recruiterSummary || profile.summary || t('imported.compareNoSummary', 'No recruiter summary is available yet.')}</p>
      </div>

      <div className="cv-compare-mini-grid">
        <div className="cv-compare-mini-card">
          <span className="cv-compare-card-label">{t('imported.topStrengths', 'Top strengths')}</span>
          <div className="cv-compare-tag-row">
            {(profile.strongestDifferentiators || []).length
              ? (profile.strongestDifferentiators || []).slice(0, 4).map((item) => <span key={item} className="cv-compare-tag success">{item}</span>)
              : <div className="cv-empty-mini">{t('imported.noUniqueSignals', 'No differentiators are available yet.')}</div>}
          </div>
        </div>

        <div className="cv-compare-mini-card warning">
          <span className="cv-compare-card-label">{t('imported.compareWatchouts', 'Missing requirements')}</span>
          <div className="cv-compare-tag-row">
            {(profile.likelyMissingRequirements || []).length
              ? (profile.likelyMissingRequirements || []).slice(0, 4).map((item) => <span key={item} className="cv-compare-tag warning">{item}</span>)
              : <div className="cv-empty-mini">{t('imported.noCompareWatchouts', 'No missing requirements are currently flagged.')}</div>}
          </div>
        </div>
      </div>

      <div className="cv-compare-detail-list">
        {signalRows.map((row) => (
          <div key={row.label} className="cv-compare-detail-row">
            <span>{uiText(row.label)}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>

      {followUpSignal && (
        <div className="cv-compare-summary-callout">
          <span className="cv-compare-card-label">{t('imported.recommendedNextStep', 'Recommended next step')}</span>
          <p>{followUpSignal}</p>
        </div>
      )}
    </div>
  );
};

const ImportedCVs = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [loading, setLoading] = useState(true);
  const [companyUID, setCompanyUID] = useState('');
  const [jobs, setJobs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showUploadManager, setShowUploadManager] = useState(false);
  const [deletingProfileId, setDeletingProfileId] = useState('');
  const [activeTab, setActiveTab] = useState('candidates');
  const [compareIds, setCompareIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [addToJobModal, setAddToJobModal] = useState({ show: false, profile: null });
  const [addJobSearch, setAddJobSearch] = useState('');
  const [selectedTargetJob, setSelectedTargetJob] = useState('');
  const [addingToJob, setAddingToJob] = useState(false);
  const [toast, setToast] = useState(null);
  const [backendSummary, setBackendSummary] = useState(null);
  const toastTimerRef = useRef(null);
  const extractionRefreshRef = useRef(new Set());
  const candidateRefreshRef = useRef(new Set());
  const batchSyncRef = useRef(false);

  const showToast = useCallback((type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message, key: Date.now() });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const reloadJobs = useCallback(async (uid) => {
    const postsSnap = await get(ref(database, `companies/${uid}/posts`));
    const jobOptions = [];

    if (postsSnap.exists()) {
      const data = postsSnap.val();
      Object.entries(data).forEach(([id, jd]) => {
        jobOptions.push({
          id,
          title: jd.jobOpening || id,
          type: 'posted_job',
          location: jd.location || jd.companyLocation || '',
          department: jd.department || ''
        });
      });
    }

    const listsSnap = await get(ref(database, `companies/${uid}/custom_lists`));
    if (listsSnap.exists()) {
      Object.values(listsSnap.val()).forEach((list) => {
        jobOptions.push({
          id: list.id,
          title: list.title,
          type: 'custom_list',
          location: list.location || '',
          department: list.department || ''
        });
      });
    }

    setJobs(jobOptions);
    return jobOptions;
  }, []);

  const refreshTalentPoolState = useCallback(async (uid = companyUID) => {
    if (!uid) return { nextProfiles: [], summary: null };

    let nextProfiles = [];
    try {
      nextProfiles = (await loadImportedTalentPoolProfiles(uid)).map(normalizeTalentProfile);
    } catch (error) {
      console.warn('Failed to load imported profiles', error);
    }

    const summary = await loadTalentPoolSummary(uid);
    const backendProfiles = Array.isArray(summary?.profiles) ? summary.profiles.map(hydrateBackendProfile) : [];
    const finalProfiles = nextProfiles.length ? nextProfiles : backendProfiles;

    setProfiles(finalProfiles);
    setBackendSummary(summary);
    setSelectedApplicant((current) => {
      if (!current?.applicantId) return current;
      return finalProfiles.find((profile) => profile.applicantId === current.applicantId) || current;
    });

    return { nextProfiles: finalProfiles, summary };
  }, [companyUID]);

  useEffect(() => {
    const init = async () => {
      if (!currentUser) return;
      setLoading(true);

      try {
        let uid = currentUser.uid;
        const ownerSnap = await get(ref(database, `companies/${uid}`));
        if (!ownerSnap.exists()) {
          const collabSnap = await get(ref(database, `collaborators/${uid}`));
          if (collabSnap.exists()) uid = collabSnap.val().companyId;
        }

        setCompanyUID(uid);
        await Promise.all([
          reloadJobs(uid),
          refreshTalentPoolState(uid)
        ]);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [currentUser, refreshTalentPoolState, reloadJobs]);

  useEffect(() => {
    setCompareIds((current) => current.filter((id) => profiles.some((profile) => profile.applicantId === id)));
  }, [profiles]);

  useEffect(() => {
    setFilterValue('');
  }, [filterBy]);

  useEffect(() => {
    const backfillExtractions = async () => {
      if (!companyUID || !profiles.length) return;

      const candidates = profiles
        .filter(shouldQueueBackgroundParsing)
        .filter((profile) => !extractionRefreshRef.current.has(profile.applicantId))
        .slice(0, 25);

      if (!candidates.length) return;

      candidates.forEach((profile) => extractionRefreshRef.current.add(profile.applicantId));

      try {
        await queueTalentPoolProfilesForParsing({
          companyUid: companyUID,
          applicantIds: candidates.map((profile) => profile.applicantId),
          source: 'imported_cvs_background_backfill',
          priority: 'normal'
        });
        await refreshTalentPoolState(companyUID);
      } catch (error) {
        console.warn('Background parsing queue failed:', error);
      }
    };

    backfillExtractions();
  }, [companyUID, profiles, refreshTalentPoolState]);

  useEffect(() => {
    if (!companyUID || !backendSummary?.parsing?.ops?.activeBatchCount) return undefined;

    let cancelled = false;
    const syncBatches = async () => {
      if (batchSyncRef.current) return;
      batchSyncRef.current = true;
      try {
        await syncTalentPoolParsingBatches({ companyUid: companyUID });
        if (!cancelled) {
          await refreshTalentPoolState(companyUID);
        }
      } catch (error) {
        console.warn('Talent-pool batch sync failed:', error);
      } finally {
        batchSyncRef.current = false;
      }
    };

    syncBatches();
    const intervalId = window.setInterval(syncBatches, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [backendSummary?.parsing?.ops?.activeBatchCount, companyUID, refreshTalentPoolState]);

  useEffect(() => {
    const hydrateSelectedCandidate = async () => {
      if (!companyUID || !selectedApplicant?.applicantId) return;
      const liveProfile = profiles.find((profile) => profile.applicantId === selectedApplicant.applicantId) || selectedApplicant;
      if (!shouldEscalateCandidateParsing(liveProfile)) return;
      if (candidateRefreshRef.current.has(liveProfile.applicantId)) return;

      candidateRefreshRef.current.add(liveProfile.applicantId);
      try {
        await extractTalentPoolFromProfile({
          companyUid: companyUID,
          applicantId: liveProfile.applicantId,
          source: 'candidate_open',
          priority: 'high',
          force: liveProfile.parsingModel === 'gpt-4.1-mini' || PARSING_ISSUE_STATUSES.has(liveProfile.parsingStatus) || liveProfile.criticalGapLabels.length > 0,
          requestedByUser: currentUser?.uid || null
        });
        await refreshTalentPoolState(companyUID);
      } catch (error) {
        console.warn('High-priority candidate parsing refresh failed:', error);
      }
    };

    hydrateSelectedCandidate();
  }, [companyUID, currentUser?.uid, profiles, refreshTalentPoolState, selectedApplicant]);

  const scopedProfiles = profiles;

  const filteredProfiles = useMemo(() => {
    let result = scopedProfiles;
    const q = searchQuery.trim().toLowerCase();
    const activeFilterValue = filterValue.trim().toLowerCase();

    if (q) {
      result = result.filter((profile) => (
        (profile.name || '').toLowerCase().includes(q)
        || (profile.email || '').toLowerCase().includes(q)
        || (profile.primaryPosition || '').toLowerCase().includes(q)
        || (profile.positionFamily || '').toLowerCase().includes(q)
        || (profile.location || '').toLowerCase().includes(q)
        || (profile.currentJobs || []).some((job) => (job || '').toLowerCase().includes(q))
        || (profile.normalizedSkills || []).some((skill) => (skill || '').toLowerCase().includes(q))
      ));
    }

    if (filterBy === 'jobList') {
      if (!activeFilterValue || activeFilterValue === 'alljoblists') {
        result = result.filter(profileHasJobAssignment);
      } else if (activeFilterValue === 'unassigned') {
        result = result.filter((profile) => !profileHasJobAssignment(profile));
      } else if (activeFilterValue) {
        const selectedJob = jobs.find((job) => String(job.id) === filterValue);
        if (selectedJob) {
          result = result.filter((profile) => profileAssignedToJob(profile, selectedJob));
        }
      }
    } else if (filterBy === 'availability' && activeFilterValue) {
      if (activeFilterValue === 'available') {
        result = result.filter((profile) => profile.activeConnectionsCount === 0);
      } else if (activeFilterValue === 'active') {
        result = result.filter((profile) => profile.activeConnectionsCount > 0);
      }
    } else if (filterBy === 'interviews' && activeFilterValue) {
      if (activeFilterValue === 'has') {
        result = result.filter((profile) => (profile.interviewCount || 0) > 0);
      } else if (activeFilterValue === 'none') {
        result = result.filter((profile) => (profile.interviewCount || 0) === 0);
      }
    } else if (filterBy === 'seniority' && activeFilterValue) {
      result = result.filter((profile) => normalizeText(profile.primarySeniority || profile.seniorityBucket).includes(activeFilterValue));
    } else if (filterBy === 'department' && activeFilterValue) {
      result = result.filter((profile) => (profile.department || '').toLowerCase().includes(activeFilterValue));
    } else if (filterBy === 'skill' && activeFilterValue) {
      result = result.filter((profile) => (profile.normalizedSkills || []).some((skill) => skill.toLowerCase().includes(activeFilterValue)));
    } else if (filterBy === 'experience' && activeFilterValue) {
      const minimumYears = Number(activeFilterValue);
      if (Number.isFinite(minimumYears) && activeFilterValue !== '') {
        result = result.filter((profile) => Number(profile.totalYearsExperience || 0) >= minimumYears);
      }
    }

    const next = [...result];
    if (sortBy === 'newest') {
      next.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    } else if (sortBy === 'lastTouched') {
      next.sort((a, b) => (a.daysSinceLastTouch || 999) - (b.daysSinceLastTouch || 999));
    } else if (sortBy === 'role') {
      next.sort((a, b) => (a.primaryPosition || a.positionFamily || '').localeCompare(b.primaryPosition || b.positionFamily || ''));
    } else if (sortBy === 'seniority') {
      next.sort((a, b) => getSeniorityRank(b.primarySeniority || b.seniorityBucket) - getSeniorityRank(a.primarySeniority || a.seniorityBucket));
    } else if (sortBy === 'activeProcesses') {
      next.sort((a, b) => (b.activeConnectionsCount || 0) - (a.activeConnectionsCount || 0));
    }

    return next;
  }, [filterBy, filterValue, jobs, scopedProfiles, searchQuery, sortBy]);
  const poolStats = useMemo(() => {
    const total = scopedProfiles.length;
    const available = scopedProfiles.filter((profile) => profile.activeConnectionsCount === 0).length;
    const active = scopedProfiles.filter((profile) => profile.activeConnectionsCount > 0).length;
    const needsEnrichment = scopedProfiles.filter((profile) => profile.needsEnrichment).length;
    const recentImports = scopedProfiles.filter((profile) => {
      const createdAt = Number(profile.createdAt || 0);
      return createdAt > 0 && (Date.now() - createdAt) <= 30 * DAY_MS;
    }).length;
    const parsedRecently = scopedProfiles.filter((profile) => {
      const parsedAt = Number(profile.lastParsedAt || 0);
      return parsedAt > 0 && (Date.now() - parsedAt) <= 7 * DAY_MS;
    }).length;
    const blockedOrFailed = scopedProfiles.filter((profile) => PARSING_ISSUE_STATUSES.has(profile.parsingStatus)).length;

    return {
      total,
      available,
      active,
      needsEnrichment,
      recentImports,
      parsedRecently,
      blockedOrFailed
    };
  }, [scopedProfiles]);

  const compareOptions = useMemo(
    () => scopedProfiles
      .slice()
      .sort((left, right) => (left.name || '').localeCompare(right.name || '')),
    [scopedProfiles]
  );

  const insights = useMemo(() => {
    const sourceProfiles = scopedProfiles.length
      ? scopedProfiles
      : (Array.isArray(backendSummary?.profiles) ? backendSummary.profiles.map(hydrateBackendProfile) : []);

    const buildDistribution = (items, fallback = []) => {
      if (items.length) return items;
      return Array.isArray(fallback) ? fallback : [];
    };

    const positionMap = {};
    const familyMap = {};
    const seniorityMap = {};
    const departmentMap = {};
    const locationMap = {};
    const skillMap = {};
    const industryMap = {};
    const languageMap = {};
    const targetRoleMap = {};
    const certificationMap = {};
    const methodologyMap = {};
    const leadershipMap = {};
    const stabilityMap = {};
    const profileShapeMap = {};
    const evidenceMap = {};
    const familySeniorityMap = {};

    sourceProfiles.forEach((profile) => {
      if (profile.primaryPosition) positionMap[profile.primaryPosition] = (positionMap[profile.primaryPosition] || 0) + 1;
      if (profile.positionFamily) familyMap[profile.positionFamily] = (familyMap[profile.positionFamily] || 0) + 1;
      if (profile.department) departmentMap[profile.department] = (departmentMap[profile.department] || 0) + 1;
      const seniority = profile.primarySeniority || profile.seniorityBucket;
      if (seniority) {
        seniorityMap[seniority] = (seniorityMap[seniority] || 0) + 1;
      }
      if (profile.location) locationMap[profile.location] = (locationMap[profile.location] || 0) + 1;

      (profile.normalizedSkills || []).forEach((skill) => {
        skillMap[skill] = (skillMap[skill] || 0) + 1;
      });

      (profile.industryTags || []).forEach((industry) => {
        industryMap[industry] = (industryMap[industry] || 0) + 1;
      });

      (profile.languages || []).forEach((language) => {
        languageMap[language] = (languageMap[language] || 0) + 1;
      });

      (profile.targetRoles || []).forEach((role) => {
        targetRoleMap[role] = (targetRoleMap[role] || 0) + 1;
      });

      (profile.certifications || []).forEach((item) => {
        certificationMap[item] = (certificationMap[item] || 0) + 1;
      });

      (profile.methodologies || []).forEach((item) => {
        methodologyMap[item] = (methodologyMap[item] || 0) + 1;
      });

      if (profile.leadershipReadiness) {
        leadershipMap[profile.leadershipReadiness] = (leadershipMap[profile.leadershipReadiness] || 0) + 1;
      }

      if (profile.stabilitySignal) {
        stabilityMap[profile.stabilitySignal] = (stabilityMap[profile.stabilitySignal] || 0) + 1;
      }

      if (profile.profileShape) {
        profileShapeMap[profile.profileShape] = (profileShapeMap[profile.profileShape] || 0) + 1;
      }

      [
        { label: t('imported.directContactCoverage', 'Direct contact'), value: profile.hasEmail || profile.hasPhone },
        { label: t('imported.interviewCoverage', 'Interview history'), value: (profile.interviewCount || 0) > 0 },
        { label: t('imported.notesCoverage', 'Recruiter notes'), value: (profile.notesCount || 0) > 0 },
        { label: t('imported.ratingsCoverage', 'Team ratings'), value: (profile.ratingCount || 0) > 0 }
      ].forEach((item) => {
        if (item.value) {
          evidenceMap[item.label] = (evidenceMap[item.label] || 0) + 1;
        }
      });

      if (profile.positionFamily && seniority) {
        familySeniorityMap[profile.positionFamily] = familySeniorityMap[profile.positionFamily] || {};
        familySeniorityMap[profile.positionFamily][seniority] = (familySeniorityMap[profile.positionFamily][seniority] || 0) + 1;
      }
    });

    const toRows = (map) => Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value }));

    const positions = buildDistribution(
      toRows(positionMap).slice(0, 8),
      backendSummary?.distributions?.positions
    );
    const positionFamilies = buildDistribution(
      toRows(familyMap).slice(0, 8),
      backendSummary?.distributions?.positionFamilies
    );
    const seniorities = buildDistribution(
      toRows(seniorityMap).slice(0, 8),
      backendSummary?.distributions?.seniorities
    );
    const departments = buildDistribution(
      toRows(departmentMap).slice(0, 8),
      backendSummary?.distributions?.departments
    );
    const locations = toRows(locationMap).slice(0, 8);
    const topSkills = toRows(skillMap).slice(0, 10);
    const industries = buildDistribution(
      toRows(industryMap).slice(0, 8),
      backendSummary?.distributions?.industries
    );
    const languages = toRows(languageMap).slice(0, 8);
    const targetRoles = toRows(targetRoleMap).slice(0, 8);
    const certifications = toRows(certificationMap).slice(0, 8);
    const methodologies = toRows(methodologyMap).slice(0, 8);
    const leadershipReadiness = toRows(leadershipMap).slice(0, 6);
    const stabilitySignals = toRows(stabilityMap).slice(0, 6);
    const profileShapes = toRows(profileShapeMap).slice(0, 6);
    const evidenceCoverage = toRows(evidenceMap).slice(0, 4);

    const familyRows = Object.entries(familySeniorityMap)
      .map(([label, values]) => ({
        label,
        total: Object.values(values).reduce((sum, value) => sum + value, 0),
        values
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);

    const seniorityKeys = seniorities.map((item) => item.label);
    const familySeniorityMatrix = familyRows.map((row) => ({
      label: row.label,
      values: seniorityKeys.map((key) => row.values[key] || 0)
    }));

    return {
      positions,
      positionFamilies,
      seniorities,
      departments,
      locations,
      topSkills,
      industries,
      languages,
      targetRoles,
      certifications,
      methodologies,
      leadershipReadiness,
      stabilitySignals,
      profileShapes,
      evidenceCoverage,
      familySeniorityMatrix
    };
  }, [backendSummary, scopedProfiles, t]);

  const comparedProfiles = useMemo(
    () => compareIds
      .map((applicantId) => profiles.find((profile) => profile.applicantId === applicantId))
      .filter(Boolean),
    [compareIds, profiles]
  );

  const comparedProfilesBySlot = useMemo(
    () => [0, 1].map((slot) => profiles.find((profile) => profile.applicantId === compareIds[slot]) || null),
    [compareIds, profiles]
  );

  const compareSharedContext = useMemo(() => {
    const [left, right] = comparedProfilesBySlot;
    if (!left || !right) {
      return { skills: [], industries: [], languages: [], targetRoles: [] };
    }

    const leftSkills = normalizeSkillValues(left.normalizedSkills || []);
    const rightSkills = normalizeSkillValues(right.normalizedSkills || []);
    const leftIndustries = canonicalizeIndustries(left.industryTags || []);
    const rightIndustries = canonicalizeIndustries(right.industryTags || []);
    const leftLanguages = normalizeLanguageValues(left.languages || []);
    const rightLanguages = normalizeLanguageValues(right.languages || []);
    const leftTargetRoles = canonicalizeRoleList(left.targetRoles || [], {
      positionFamily: left.positionFamily,
      fallback: [left.primaryPosition]
    });
    const rightTargetRoles = canonicalizeRoleList(right.targetRoles || [], {
      positionFamily: right.positionFamily,
      fallback: [right.primaryPosition]
    });

    return {
      skills: intersectNormalizedLabels(leftSkills, rightSkills, 8),
      industries: intersectNormalizedLabels(leftIndustries, rightIndustries, 6),
      languages: intersectNormalizedLabels(leftLanguages, rightLanguages, 6),
      targetRoles: intersectNormalizedLabels(leftTargetRoles, rightTargetRoles, 6)
    };
  }, [comparedProfilesBySlot]);

  const compareDuelMetrics = useMemo(() => {
    const [left, right] = comparedProfilesBySlot;
    if (!left || !right) return [];

    return [
      {
        key: 'experience',
        label: t('imported.experience', 'Experience'),
        leftValue: left.totalYearsExperience || 0,
        rightValue: right.totalYearsExperience || 0
      },
      {
        key: 'skills',
        label: t('imported.skillDepth', 'Skill depth'),
        leftValue: (left.normalizedSkills || []).length,
        rightValue: (right.normalizedSkills || []).length
      },
      {
        key: 'domains',
        label: t('imported.domainDepth', 'Domain depth'),
        leftValue: (left.domainExposure || []).length,
        rightValue: (right.domainExposure || []).length
      },
      {
        key: 'credentials',
        label: t('imported.credentials', 'Credentials'),
        leftValue: (left.certifications || []).length,
        rightValue: (right.certifications || []).length
      }
    ];
  }, [comparedProfilesBySlot, t]);

  const industryOptions = useMemo(
    () => ['all', ...dedupe(scopedProfiles.flatMap((profile) => profile.industryTags || [])).sort((left, right) => left.localeCompare(right))],
    [scopedProfiles]
  );

  useEffect(() => {
    if (selectedIndustry !== 'all' && !industryOptions.includes(selectedIndustry)) {
      setSelectedIndustry('all');
    }
  }, [industryOptions, selectedIndustry]);

  const marketView = useMemo(() => {
    const lensProfiles = selectedIndustry === 'all'
      ? scopedProfiles
      : scopedProfiles.filter((profile) => (profile.industryTags || []).includes(selectedIndustry));

    return {
      selectedIndustry,
      scopedCount: lensProfiles.length,
      availableCount: lensProfiles.filter((profile) => profile.activeConnectionsCount === 0).length,
      industries: toDistributionRows(scopedProfiles.flatMap((profile) => profile.industryTags || []), 10),
      departments: toDistributionRows(lensProfiles.map((profile) => profile.department), 8),
      positionFamilies: toDistributionRows(lensProfiles.map((profile) => profile.positionFamily || profile.primaryPosition), 8),
      seniorities: toDistributionRows(lensProfiles.map((profile) => profile.primarySeniority || profile.seniorityBucket), 8),
      locations: toDistributionRows(lensProfiles.map((profile) => profile.location), 8),
      adjacentRoles: toDistributionRows(lensProfiles.flatMap((profile) => profile.adjacentRoles || []), 8),
      leadershipReadiness: toDistributionRows(lensProfiles.map((profile) => profile.leadershipReadiness), 6),
      languages: toDistributionRows(lensProfiles.flatMap((profile) => profile.languages || []), 8),
      workAuthorization: toDistributionRows(lensProfiles.map((profile) => profile.workAuthorization), 6)
    };
  }, [scopedProfiles, selectedIndustry]);

  const filteredModalJobs = useMemo(() => {
    const q = addJobSearch.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) => (job.title || '').toLowerCase().includes(q));
  }, [jobs, addJobSearch]);

  const chartTextColor = '#d9e6ff';
  const gridColor = 'rgba(255, 255, 255, 0.08)';

  const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: chartTextColor,
          font: { family: 'Montserrat', size: 11 }
        }
      },
      tooltip: {
        backgroundColor: '#081a34',
        borderColor: 'rgba(86, 141, 237, 0.35)',
        borderWidth: 1,
        titleColor: '#ffffff',
        bodyColor: '#d9e6ff'
      }
    },
    scales: {
      x: {
        ticks: { color: chartTextColor, font: { family: 'Montserrat', size: 11 } },
        grid: { color: gridColor }
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { family: 'Montserrat', size: 11 } },
        grid: { color: gridColor }
      }
    }
  };

  const doughnutChartOptions = {
    ...baseChartOptions,
    scales: undefined,
    plugins: {
      ...baseChartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          color: chartTextColor,
          font: { family: 'Montserrat', size: 11 }
        }
      }
    }
  };

  const horizontalBarOptions = {
    ...baseChartOptions,
    indexAxis: 'y'
  };

  const stackedBarOptions = {
    ...baseChartOptions,
    scales: {
      x: {
        stacked: true,
        ticks: { color: chartTextColor, font: { family: 'Montserrat', size: 11 } },
        grid: { color: gridColor }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { family: 'Montserrat', size: 11 } },
        grid: { color: gridColor }
      }
    }
  };

  const polarChartOptions = {
    ...doughnutChartOptions,
    scales: undefined
  };

  const buildBarData = (rows, label = t('imported.candidates', 'Candidates')) => ({
    labels: rows.map((item) => item.label),
    datasets: [
      {
        label,
        data: rows.map((item) => item.value),
        backgroundColor: rows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
        borderRadius: 10
      }
    ]
  });

  const buildDoughnutData = (rows) => ({
    labels: rows.map((item) => item.label),
    datasets: [
      {
        data: rows.map((item) => item.value),
        backgroundColor: rows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
        borderColor: '#081a34',
        borderWidth: 2
      }
    ]
  });

  const positionChartData = buildBarData(insights.positions.slice(0, 8));
  const familyChartData = buildDoughnutData(insights.positionFamilies.slice(0, 8));
  const seniorityChartData = buildDoughnutData(insights.seniorities.slice(0, 8));
  const departmentChartData = buildDoughnutData(insights.departments.slice(0, 8));
  const skillChartData = buildBarData(insights.topSkills.slice(0, 10), t('imported.profileCount', 'Profiles'));
  const locationChartData = buildBarData(insights.locations.slice(0, 8));
  const languageChartData = buildDoughnutData(insights.languages.slice(0, 8));
  const targetRoleChartData = buildBarData(insights.targetRoles.slice(0, 8), t('imported.profileCount', 'Profiles'));
  const certificationChartData = buildBarData(insights.certifications.slice(0, 8), t('imported.profileCount', 'Profiles'));
  const methodologyChartData = buildBarData(insights.methodologies.slice(0, 8), t('imported.profileCount', 'Profiles'));
  const leadershipReadinessChartData = buildDoughnutData(insights.leadershipReadiness.slice(0, 6));
  const stabilitySignalChartData = buildDoughnutData(insights.stabilitySignals.slice(0, 6));
  const profileShapeChartData = buildDoughnutData(insights.profileShapes.slice(0, 6));
  const evidenceCoverageChartData = buildBarData(insights.evidenceCoverage, t('imported.profileCount', 'Profiles'));
  const industryChartData = buildDoughnutData(insights.industries.slice(0, 8));
  const familySeniorityChartData = {
    labels: insights.familySeniorityMatrix.map((row) => row.label),
    datasets: insights.seniorities.map((seniority, index) => ({
      label: seniority.label,
      data: insights.familySeniorityMatrix.map((row) => row.values[index] || 0),
      backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
      borderRadius: 8
    }))
  };

  const marketIndustryData = buildDoughnutData(marketView.industries);
  const marketDepartmentData = buildDoughnutData(marketView.departments);
  const marketFamilyData = buildBarData(marketView.positionFamilies, t('imported.profileCount', 'Profiles'));
  const marketSeniorityData = buildDoughnutData(marketView.seniorities);
  const marketLocationData = buildBarData(marketView.locations, t('imported.profileCount', 'Profiles'));
  const marketLeadershipData = buildDoughnutData(marketView.leadershipReadiness);
  const marketWorkAuthData = buildDoughnutData(marketView.workAuthorization);
  const marketAdjacentRoleData = buildBarData(marketView.adjacentRoles, t('imported.profileCount', 'Profiles'));
  const marketLanguageData = buildBarData(marketView.languages, t('imported.profileCount', 'Profiles'));

  const downloadCV = (profile) => {
    if (!profile.cvLink) {
      showToast('error', t('imported.noCv'));
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = profile.cvLink;
      link.target = '_blank';
      if (profile.fileName) link.setAttribute('download', profile.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      fetch(profile.cvLink)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = profile.fileName || 'cv.pdf';
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          try {
            window.open(profile.cvLink, '_blank', 'noopener');
          } catch (_) {
            showToast('error', t('imported.downloadFailed'));
          }
        });
    }
  };

  const handleDeleteProfile = useCallback(async (profile, event) => {
    if (event) event.stopPropagation();
    if (!companyUID || !profile?.applicantId) return;

    const confirmed = window.confirm(
      t('imported.confirmDeleteProfile', {
        name: profile.name,
        defaultValue: `Delete ${profile.name} from the imported CVs list? This action cannot be undone.`
      })
    );
    if (!confirmed) return;

    setDeletingProfileId(profile.applicantId);

    try {
      const updates = {
        [`imported_profiles/${companyUID}/${profile.applicantId}`]: null,
        [`imported_profiles/${profile.applicantId}`]: null
      };

      (profile.connectionKeys || []).forEach((connectionKey) => {
        updates[`connections/${connectionKey}`] = null;
      });

      (profile.activeConnections || []).forEach((connection) => {
        if (!connection?.connectionKey || !connection?.jobId) return;
        updates[`companies/${companyUID}/applicant_index/by_job/${connection.jobId}/${connection.connectionKey}`] = null;
      });

      await update(ref(database), updates);
      await refreshTalentPoolState(companyUID);
      setSelectedApplicant((current) => (current?.applicantId === profile.applicantId ? null : current));
      showToast('success', t('imported.profileDeleted', {
        name: profile.name,
        defaultValue: `${profile.name} deleted`
      }));
    } catch (error) {
      console.error(error);
      showToast('error', t('imported.profileDeleteFailed', 'Failed to delete candidate'));
    } finally {
      setDeletingProfileId('');
    }
  }, [companyUID, refreshTalentPoolState, showToast, t]);

  const openAddToJobModal = (profile, event) => {
    if (event) event.stopPropagation();
    setAddToJobModal({ show: true, profile });
    setAddJobSearch('');
    setSelectedTargetJob('');
  };

  const closeAddToJobModal = () => {
    setAddToJobModal({ show: false, profile: null });
    setAddJobSearch('');
    setSelectedTargetJob('');
  };

  const confirmAddToJob = async () => {
    const profile = addToJobModal.profile;
    if (!profile || !selectedTargetJob) return;
    setAddingToJob(true);

    try {
      const targetJob = jobs.find((job) => job.id === selectedTargetJob);
      const jobTitle = targetJob?.title || selectedTargetJob;
      const isCustomList = targetJob?.type === 'custom_list';
      const newKey = `${companyUID}_imported_${profile.applicantId}_copy_${Date.now()}`;
      const timestamp = Date.now();
      const updates = {};

      updates[`connections/${newKey}`] = {
        employerUid: companyUID,
        tempProfileId: profile.applicantId,
        applicantType: 'imported',
        jobId: selectedTargetJob,
        jobAppliedFor: jobTitle,
        stageId: 'stage-1',
        substageId: 'all-applicants-1',
        canMessage: false,
        source: 'cv_upload',
        isCustomList: !!isCustomList,
        createdAt: timestamp,
        lastStageChange: new Date().toISOString()
      };

      updates[`companies/${companyUID}/applicant_index/by_job/${selectedTargetJob}/${newKey}`] = {
        applicantId: profile.applicantId,
        applicantType: 'imported',
        applicantName: profile.name,
        applicantEmail: profile.email,
        jobTitle,
        isCustomList: !!isCustomList,
        stageId: 'stage-1',
        substageId: 'all-applicants-1',
        passedFilter: false,
        lastUpdated: timestamp,
        createdAt: timestamp,
        source: 'cv_upload'
      };

      const profileRef = ref(database, `imported_profiles/${companyUID}/${profile.applicantId}`);
      const profileSnap = await get(profileRef);
      let currentAssignedJobs = [];
      if (profileSnap.exists() && profileSnap.val().assigned_jobs) {
        currentAssignedJobs = profileSnap.val().assigned_jobs;
      }
      if (!currentAssignedJobs.includes(jobTitle)) {
        currentAssignedJobs.push(jobTitle);
      }
      updates[`imported_profiles/${companyUID}/${profile.applicantId}/assigned_jobs`] = currentAssignedJobs;

      await update(ref(database), updates);

      try {
        await syncCommunityMember(currentUser, {
          member_id: `imported_${profile.applicantId}`,
          member_type: 'imported_candidate',
          imported_profile_id: profile.applicantId,
          applicant_id: profile.applicantId,
          connection_key: newKey,
          email: profile.email || '',
          channels: { talent_pool: true }
        });
      } catch (syncError) {
        console.warn('Community talent-pool sync failed:', syncError);
      }

      try {
        await triggerCommunityAutoSync(currentUser);
      } catch (syncError) {
        console.warn('Community auto-sync failed:', syncError);
      }

      await refreshTalentPoolState(companyUID);
      closeAddToJobModal();
      showToast('success', t('imported.addSuccess', {
        name: profile.name,
        job: jobTitle,
        defaultValue: `${profile.name} added to ${jobTitle}`
      }));
    } catch (error) {
      console.error(error);
      showToast('error', t('imported.addFailed'));
    } finally {
      setAddingToJob(false);
    }
  };

  const setCompareSlot = useCallback((slotIndex, applicantId) => {
    setCompareIds((current) => {
      const next = [...current];

      if (!applicantId) {
        next.splice(slotIndex, 1);
        return next.filter(Boolean).slice(0, MAX_COMPARE_CANDIDATES);
      }

      const existingIndex = next.indexOf(applicantId);
      if (existingIndex >= 0) {
        next.splice(existingIndex, 1);
      }

      next[slotIndex] = applicantId;
      return next.filter(Boolean).slice(0, MAX_COMPARE_CANDIDATES);
    });
  }, []);

  const handleManualReprocess = useCallback(async (profile, options = {}) => {
    if (!companyUID || !profile?.applicantId) return;

    try {
      await extractTalentPoolFromProfile({
        companyUid: companyUID,
        applicantId: profile.applicantId,
        source: options.source || 'manual_reprocess',
        priority: 'high',
        force: options.force ?? true,
        requestedByUser: currentUser?.uid || null
      });
      extractionRefreshRef.current.add(profile.applicantId);
      candidateRefreshRef.current.add(profile.applicantId);
      await refreshTalentPoolState(companyUID);
      showToast('success', t('imported.reprocessQueued', 'CV reprocessing queued'));
    } catch (error) {
      console.warn('Manual CV reprocess failed:', error);
      showToast('error', t('imported.reprocessFailed', 'Failed to queue CV reprocessing'));
      throw error;
    }
  }, [companyUID, currentUser?.uid, refreshTalentPoolState, showToast, t]);

  const summaryCards = [
    {
      key: 'total',
      color: '#568DED',
      icon: 'fa-users',
      label: t('imported.totalTalentPool', 'Total Talent Pool'),
      value: poolStats.total
    },
    {
      key: 'available',
      color: '#4CAF50',
      icon: 'fa-user-check',
      label: t('imported.availableInPool', 'Available in Pool'),
      value: poolStats.available
    },
    {
      key: 'recent',
      color: '#FF9800',
      icon: 'fa-clock-rotate-left',
      label: t('imported.recentlyAdded', 'Recently Added'),
      value: poolStats.recentImports
    }
  ];

  const renderCandidatesTab = () => {
    if (loading) {
      return (
        <div className="cv-loading">
          <i className="fa-solid fa-spinner fa-spin" />
        </div>
      );
    }

    if (filteredProfiles.length === 0) {
      return (
        <div className="cv-empty-state">
          <div className="cv-empty-icon">
            <i className="fa-solid fa-folder-open" />
          </div>
          <div className="cv-empty-title">{t('imported.empty')}</div>
          <div className="cv-empty-subtitle">
            {searchQuery
              ? t('imported.noSearchResults', 'Try adjusting your search or filters')
              : t('imported.emptyHint', 'Upload CVs to start building your talent pool')}
          </div>
          {!searchQuery && (
            <button className="post-job-cta" onClick={() => setShowUploadManager(true)}>
              <i className="fa-solid fa-circle-plus" />
              <span>{t('imported.uploadCta')}</span>
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="cv-card-grid">
        {filteredProfiles.map((profile) => (
          <CandidateCard
            key={profile.applicantId}
            profile={profile}
            t={t}
            openAddToJobModal={openAddToJobModal}
            downloadCV={downloadCV}
            setSelectedApplicant={setSelectedApplicant}
            handleDeleteProfile={handleDeleteProfile}
            deletingProfileId={deletingProfileId}
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      <div
        className={`cv-body${isRTL ? ' rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          '--sidebar-w': sidebarCollapsed ? '60px' : '240px',
          '--body-side-pad': '40px'
        }}
      >
        <FeatureGate showBanner={false}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h2 className="cv-header">{t('imported.title')}</h2>
              <p className="cv-subtitle">{t('imported.subtitle', 'Your centralized talent pool — manage, search, and assign candidates across jobs')}</p>
            </div>
          </div>

          {activeTab === 'candidates' && (
            <SummaryStrip stats={summaryCards} />
          )}

          <div className={`cv-tabs-row${activeTab !== 'candidates' ? ' standalone' : ''}`}>
            <div className="cv-tabs">
              {[
                { key: 'candidates', label: t('imported.tabCandidates', 'Candidates') },
                { key: 'insights', label: t('imported.tabInsights', 'Insights') },
                { key: 'compare', label: t('imported.tabCompare', 'Compare') },
                { key: 'market', label: t('imported.tabMarket', 'Market') }
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`cv-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {uiText(tab.label)}
                </button>
              ))}
            </div>

            <div className="cv-tabs-actions">
              <button
                className="post-job-cta"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setShowUploadManager(true)}
              >
                <i className="fa-solid fa-circle-plus" />
                <span>{t('imported.uploadCta')}</span>
              </button>
            </div>
          </div>

          {activeTab === 'candidates' && (
            <>
              <div className="cv-toolbar">
                <div className="cv-search">
                  <i className="fa-solid fa-magnifying-glass" />
                  <input
                    type="text"
                    placeholder={t('imported.search', 'Search candidates...')}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>

                <select className="cv-sort-select" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  <option value="newest">{t('imported.sortNewest', 'Newest first')}</option>
                  <option value="lastTouched">{t('imported.sortLastTouched', 'Last touched')}</option>
                  <option value="role">{t('imported.sortRole', 'Role')}</option>
                  <option value="seniority">{t('imported.sortSeniority', 'Seniority')}</option>
                  <option value="activeProcesses">{t('imported.sortActiveProcesses', 'Active workflow count')}</option>
                </select>

                <select
                  className="cv-sort-select cv-filter-by-select"
                  value={filterBy}
                  onChange={(event) => {
                    const nextFilter = event.target.value;
                    setFilterBy(nextFilter);
                    setFilterValue(nextFilter === 'jobList' ? 'allJobLists' : '');
                  }}
                >
                  <option value="">{t('imported.filterBy', 'Filter by')}</option>
                  <option value="jobList">{t('imported.filterJobList', 'Assigned to job list')}</option>
                  <option value="availability">{t('imported.filterAvailability', 'Availability')}</option>
                  <option value="interviews">{t('imported.filterInterviews', 'Interviews')}</option>
                  <option value="seniority">{t('imported.filterSeniority', 'Seniority')}</option>
                  <option value="department">{t('imported.filterDepartment', 'Department')}</option>
                  <option value="experience">{t('imported.filterExperience', 'Years of experience')}</option>
                  <option value="skill">{t('imported.filterSkill', 'Skill')}</option>
                </select>

                {filterBy === 'jobList' && (
                  <select
                    className="cv-sort-select cv-filter-value-select"
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  >
                    <option value="allJobLists">{t('imported.allJobLists', 'All job lists')}</option>
                    <option value="unassigned">{t('imported.unassigned', 'Unassigned')}</option>
                    {jobs.map((job) => (
                      <option key={job.id} value={job.id}>{job.title}</option>
                    ))}
                  </select>
                )}

                {filterBy === 'availability' && (
                  <select
                    className="cv-sort-select cv-filter-value-select"
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  >
                    <option value="">{t('imported.chooseAvailability', 'Choose availability')}</option>
                    <option value="available">{t('imported.filterAvailable', 'Available')}</option>
                    <option value="active">{t('imported.filterActiveShort', 'Active ATS')}</option>
                  </select>
                )}

                {filterBy === 'interviews' && (
                  <select
                    className="cv-sort-select cv-filter-value-select"
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  >
                    <option value="">{t('imported.chooseInterviews', 'Choose interview filter')}</option>
                    <option value="has">{t('imported.withInterviews', 'Has interviews')}</option>
                    <option value="none">{t('imported.noInterviews', 'No interviews')}</option>
                  </select>
                )}

                {filterBy === 'seniority' && (
                  <select
                    className="cv-sort-select cv-filter-value-select"
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  >
                    <option value="">{t('imported.chooseSeniority', 'Choose seniority')}</option>
                    <option value="junior">{t('imported.junior', 'Junior')}</option>
                    <option value="mid">{t('imported.mid', 'Mid')}</option>
                    <option value="senior">{t('imported.senior', 'Senior')}</option>
                    <option value="lead">{t('imported.leadership', 'Leadership')}</option>
                  </select>
                )}

                {filterBy === 'department' && (
                  <input
                    type="text"
                    className="cv-filter-inline-input"
                    placeholder={t('imported.filterDepartmentPlaceholder', 'Type a department')}
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  />
                )}

                {filterBy === 'experience' && (
                  <input
                    type="number"
                    min="0"
                    className="cv-filter-inline-input"
                    placeholder={t('imported.filterExperiencePlaceholder', 'Minimum years')}
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  />
                )}

                {filterBy === 'skill' && (
                  <input
                    type="text"
                    className="cv-filter-inline-input"
                    placeholder={t('imported.filterSkillPlaceholder', 'Type a skill')}
                    value={filterValue}
                    onChange={(event) => setFilterValue(event.target.value)}
                  />
                )}

                {filterBy && (
                  <button
                    type="button"
                    className="cv-filter-clear-btn"
                    onClick={() => {
                      setFilterBy('');
                      setFilterValue('');
                    }}
                  >
                    <i className="fa-solid fa-xmark" />
                    <span>{t('imported.clearFilter', 'Clear filter')}</span>
                  </button>
                )}
              </div>

              {renderCandidatesTab()}
            </>
          )}

          {activeTab === 'insights' && (
            <div className="cv-grid-layout">
              <Panel
                title={t('imported.poolComposition', 'Role and department composition')}
                subtitle={t('imported.poolCompositionSubtitle', 'How this talent pool is distributed across roles, teams, and seniority levels.')}
                wide
              >
                <div className="cv-visual-grid two">
                  <div className="cv-chart-shell medium">
                    {insights.positions.length
                      ? <Bar data={positionChartData} options={baseChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noPositionDistribution', 'No extracted position data yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.positionFamilies.length
                      ? <Doughnut data={familyChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noFamilyDistribution', 'No position-family data yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.seniorities.length
                      ? <Doughnut data={seniorityChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noSeniorityDistribution', 'No extracted seniority data yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.departments.length
                      ? <Doughnut data={departmentChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noDepartmentDistribution', 'No department mix is available yet.')}</div>}
                  </div>
                </div>
              </Panel>

              <Panel
                title={t('imported.familySeniorityTitle', 'Role-family seniority mix')}
                subtitle={t('imported.familySenioritySubtitle', 'Where the pool is deepest by role family and seniority at the same time.')}
                wide
              >
                <div className="cv-chart-shell medium">
                  {insights.familySeniorityMatrix.length
                    ? <Bar data={familySeniorityChartData} options={stackedBarOptions} />
                    : <div className="cv-empty-mini">{t('imported.noFamilySeniority', 'No position-family and seniority mix is available yet.')}</div>}
                </div>
              </Panel>

              <Panel
                title={t('imported.skillAndIndustryTitle', 'Skill and industry mix')}
                subtitle={t('imported.skillAndIndustrySubtitle', 'Where the pool is strongest in capabilities and domain exposure.')}
                wide
              >
                <div className="cv-visual-grid two">
                  <div className="cv-chart-shell medium">
                    {insights.topSkills.length
                      ? <Bar data={skillChartData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noSkillDistribution', 'No normalized skill distribution is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.industries.length
                      ? <PolarArea data={industryChartData} options={polarChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noIndustryDistribution', 'No industry concentration is available yet.')}</div>}
                  </div>
                </div>
              </Panel>

              <Panel
                title={t('imported.credentialsAndSpecializationTitle', 'Credentials and specialization')}
                subtitle={t('imported.credentialsAndSpecializationSubtitle', 'What this pool is calibrated for beyond titles alone: target roles, certifications, and delivery methodologies.')}
                wide
              >
                <div className="cv-visual-grid three">
                  <div className="cv-chart-shell medium">
                    {insights.targetRoles.length
                      ? <Bar data={targetRoleChartData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noTargetRoleDistribution', 'No extracted target-role distribution is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.certifications.length
                      ? <Bar data={certificationChartData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noCertificationDistribution', 'No certification signal is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.methodologies.length
                      ? <Bar data={methodologyChartData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noMethodologyDistribution', 'No methodology signal is available yet.')}</div>}
                  </div>
                </div>
              </Panel>

              <Panel
                title={t('imported.trajectorySignalsTitle', 'Trajectory and leadership signals')}
                subtitle={t('imported.trajectorySignalsSubtitle', 'What the extracted CV history suggests about leadership readiness, stability, and candidate profile shape.')}
                wide
              >
                <div className="cv-visual-grid three">
                  <div className="cv-chart-shell medium">
                    {insights.leadershipReadiness.length
                      ? <Doughnut data={leadershipReadinessChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noLeadershipDistribution', 'No leadership-readiness distribution is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.stabilitySignals.length
                      ? <Doughnut data={stabilitySignalChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noStabilityDistribution', 'No stability signal distribution is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.profileShapes.length
                      ? <Doughnut data={profileShapeChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noProfileShapeDistribution', 'No profile-shape signal is available yet.')}</div>}
                  </div>
                </div>
              </Panel>

              <Panel
                title={t('imported.geographyAndReachTitle', 'Geography, language, and recruiter evidence')}
                subtitle={t('imported.geographyAndReachSubtitle', 'The places, language coverage, and recruiter evidence signals available in this slice of the pool.')}
                wide
              >
                <div className="cv-visual-grid three">
                  <div className="cv-chart-shell medium">
                    {insights.locations.length
                      ? <Bar data={locationChartData} options={baseChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noLocationDistribution', 'No location distribution is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.languages.length
                      ? <Doughnut data={languageChartData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noLanguageDistribution', 'No language distribution is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {insights.evidenceCoverage.length
                      ? <Bar data={evidenceCoverageChartData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noEvidenceCoverage', 'No recruiter evidence coverage is available yet.')}</div>}
                  </div>
                </div>
              </Panel>
            </div>
          )}

          {activeTab === 'compare' && (
            <div className="cv-grid-layout">
              <Panel
                title={t('imported.compareTitle', 'Candidate comparison')}
                subtitle={t('imported.compareSubtitle', 'Compare the selected candidates on extracted role, specialization, and career-trajectory signals.')}
                wide
                variant="compare"
                actions={compareIds.length ? (
                  <button type="button" className="cv-text-btn" onClick={() => setCompareIds([])}>
                    {t('imported.clearCompare', 'Clear')}
                  </button>
                ) : null}
              >
                <div className="cv-compare-picker">
                  {[0, 1].map((slot) => (
                    <label key={slot} className="cv-compare-select-shell">
                      <span>{slot === 0 ? t('imported.firstCandidate', 'First candidate') : t('imported.secondCandidate', 'Second candidate')}</span>
                      <select
                        className="cv-filter-select"
                        value={compareIds[slot] || ''}
                        onChange={(event) => setCompareSlot(slot, event.target.value)}
                      >
                        <option value="">{t('imported.selectCandidate', 'Select candidate')}</option>
                        {compareOptions.map((profile) => (
                          <option key={profile.applicantId} value={profile.applicantId}>
                            {profile.name} {profile.primaryPosition ? `• ${profile.primaryPosition}` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>

                {!comparedProfiles.length ? (
                  <div className="cv-compare-empty">
                    {t('imported.compareEmpty', 'Pick up to two candidates to compare them side by side.')}
                  </div>
                ) : (
                  <div className="cv-compare-layout">
                    <CompareBlock>
                      <CompareSectionHeader
                        title={t('imported.compareOverviewHeader', 'Candidate overview')}
                        subtitle={t('imported.compareOverviewSubtitle', 'A side-by-side read on role, seniority, company context, domain focus, and direct reachability.')}
                      />
                      <div className="cv-compare-hero-grid">
                        <CompareHeroCard profile={comparedProfilesBySlot[0]} t={t} />
                        <CompareHeroCard profile={comparedProfilesBySlot[1]} t={t} />
                      </div>
                    </CompareBlock>

                    <CompareBlock>
                      <CompareSectionHeader
                        title={t('imported.compareMetricsHeader', 'Key figures')}
                        subtitle={t('imported.compareMetricsSubtitle', 'A fast numerical read on extracted experience, specialization breadth, and credential depth.')}
                      />
                      <div className="cv-compare-duel-grid">
                        {compareDuelMetrics.map((metric) => (
                          <CompareMetricDuel
                            key={metric.key}
                            label={uiText(metric.label)}
                            leftValue={metric.leftValue}
                            rightValue={metric.rightValue}
                            leftName={comparedProfilesBySlot[0]?.name || t('imported.firstCandidate', 'First candidate')}
                            rightName={comparedProfilesBySlot[1]?.name || t('imported.secondCandidate', 'Second candidate')}
                          />
                        ))}
                      </div>
                    </CompareBlock>

                    <CompareBlock>
                      <CompareSectionHeader
                        title={t('imported.compareSharedContextHeader', 'Shared context')}
                        subtitle={t('imported.compareSharedContextSubtitle', 'The strongest overlap between both candidates across skills, industries, languages, and target-role intent.')}
                      />
                      <div className="cv-compare-section-grid two compact">
                        <CompareListBlock
                          title={t('imported.sharedSkills', 'Shared skills')}
                          items={compareSharedContext.skills}
                          empty={t('imported.noSharedSkills', 'No shared normalized skills yet.')}
                        />
                        <CompareListBlock
                          title={t('imported.sharedIndustries', 'Shared industries')}
                          items={compareSharedContext.industries}
                          empty={t('imported.noSharedIndustries', 'No shared industries are standardized yet.')}
                        />
                      </div>
                      <div className="cv-compare-section-grid two compact">
                        <CompareListBlock
                          title={t('imported.sharedLanguages', 'Shared languages')}
                          items={compareSharedContext.languages}
                          empty={t('imported.noSharedLanguages', 'No shared language coverage is available yet.')}
                        />
                        <CompareListBlock
                          title={t('imported.sharedTargetRoles', 'Shared target roles')}
                          items={compareSharedContext.targetRoles}
                          empty={t('imported.noSharedTargetRoles', 'No shared target roles are available yet.')}
                        />
                      </div>
                    </CompareBlock>

                    <CompareBlock>
                      <CompareSectionHeader
                        title={t('imported.compareRecruiterReadHeader', 'Candidate reads')}
                        subtitle={t('imported.compareRecruiterReadSubtitle', 'How each profile reads once you include extracted trajectory, management scope, credentials, and next-step guidance.')}
                      />
                      <div className="cv-compare-section-grid two compact">
                        <CompareCandidateLens
                          profile={comparedProfilesBySlot[0]}
                          title={comparedProfilesBySlot[0]?.name || t('imported.firstCandidate', 'First candidate')}
                          t={t}
                        />
                        <CompareCandidateLens
                          profile={comparedProfilesBySlot[1]}
                          title={comparedProfilesBySlot[1]?.name || t('imported.secondCandidate', 'Second candidate')}
                          t={t}
                        />
                      </div>
                    </CompareBlock>
                  </div>
                )}
              </Panel>
            </div>
          )}

          {activeTab === 'market' && (
            <div className="cv-grid-layout">
              <Panel
                title={t('imported.marketOpportunityTitle', 'Industry lens')}
                subtitle={t('imported.marketOpportunitySubtitle', 'Filter the talent pool by industry to see where this segment is concentrated across departments and role families.')}
                wide
                actions={(
                  <select
                    className="cv-filter-select"
                    value={selectedIndustry}
                    onChange={(event) => setSelectedIndustry(event.target.value)}
                  >
                    <option value="all">{t('imported.allIndustries', 'All industries')}</option>
                    {industryOptions.filter((item) => item !== 'all').map((industry) => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                )}
              >
                <div className="cv-market-summary">
                  <span>
                    {t('imported.marketScopedProfiles', {
                      count: marketView.scopedCount,
                      defaultValue: `${marketView.scopedCount} profiles in this industry lens`
                    })}
                  </span>
                  <strong>
                    {t('imported.marketAvailableProfiles', {
                      count: marketView.availableCount,
                      defaultValue: `${marketView.availableCount} available in pool`
                    })}
                  </strong>
                </div>
                <div className="cv-visual-grid two">
                  <div className="cv-chart-shell medium">
                    {marketView.industries.length
                      ? <Doughnut data={marketIndustryData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noIndustryMarketData', 'No industry depth signal is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {marketView.departments.length
                      ? <Doughnut data={marketDepartmentData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noDepartmentMarketData', 'No department mix is available for this industry lens yet.')}</div>}
                  </div>
                </div>
              </Panel>

              <Panel
                title={t('imported.marketDepthTitle', 'Role and seniority depth')}
                subtitle={t('imported.marketDepthSubtitle', 'The standardized role families, seniority mix, and locations that define this segment of the pool.')}
                wide
              >
                <div className="cv-visual-grid three">
                  <div className="cv-chart-shell medium">
                    {marketView.positionFamilies.length
                      ? <Bar data={marketFamilyData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noReachableFamilyData', 'No role-family spread is available for this lens yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {marketView.seniorities.length
                      ? <Doughnut data={marketSeniorityData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noSeniorityMarketData', 'No seniority mix is available for this lens yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {marketView.locations.length
                      ? <Bar data={marketLocationData} options={baseChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noLocationMarketData', 'No location spread is available for this lens yet.')}</div>}
                  </div>
                </div>
              </Panel>

              <Panel
                title={t('imported.marketAdjacencyTitle', 'Adjacent opportunity signals')}
                subtitle={t('imported.marketAdjacencySubtitle', 'Standardized adjacent roles, leadership mix, and practical hiring coverage for the selected industry lens.')}
                wide
              >
                <div className="cv-visual-grid three">
                  <div className="cv-chart-shell medium">
                    {marketView.adjacentRoles.length
                      ? <Bar data={marketAdjacentRoleData} options={horizontalBarOptions} />
                      : <div className="cv-empty-mini">{t('imported.noAdjacentRoles', 'No adjacent-role spread is available yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {marketView.leadershipReadiness.length
                      ? <Doughnut data={marketLeadershipData} options={doughnutChartOptions} />
                      : <div className="cv-empty-mini">{t('imported.noLeadershipReadinessData', 'No leadership signal is available for this lens yet.')}</div>}
                  </div>
                  <div className="cv-chart-shell medium">
                    {marketView.languages.length
                      ? <Bar data={marketLanguageData} options={horizontalBarOptions} />
                      : marketView.workAuthorization.length
                        ? <Doughnut data={marketWorkAuthData} options={doughnutChartOptions} />
                        : <div className="cv-empty-mini">{t('imported.noLanguageMarketData', 'No language or authorization signal is available for this lens yet.')}</div>}
                  </div>
                </div>
              </Panel>
            </div>
          )}

          <CVUploadManager
            isOpen={showUploadManager}
            onClose={() => setShowUploadManager(false)}
            employerUID={companyUID}
            onUploadComplete={async () => {
              setShowUploadManager(false);
              await reloadJobs(companyUID);
              await refreshTalentPoolState(companyUID);
            }}
            onDataRefresh={async () => {
              await reloadJobs(companyUID);
              await refreshTalentPoolState(companyUID);
            }}
            allowNoJob={true}
          />

          {selectedApplicant && (
            <ImportedApplicantDetailsPopup
              applicant={selectedApplicant}
              onClose={() => setSelectedApplicant(null)}
              onUpdate={async () => {
                await refreshTalentPoolState(companyUID);
              }}
              onRequestReprocess={handleManualReprocess}
            />
          )}
        </FeatureGate>
      </div>

      {addToJobModal.show && (
        <div className="cv-modal-overlay" onClick={closeAddToJobModal}>
          <div className="cv-modal" onClick={(event) => event.stopPropagation()}>
            <div className="cv-modal-header">
              <h3>{t('imported.addToJobTitle', 'Add candidate to job')}</h3>
              <button type="button" className="cv-modal-close" onClick={closeAddToJobModal}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="cv-modal-search">
              <input
                type="text"
                placeholder={t('imported.searchJobs', 'Search jobs or lists...')}
                value={addJobSearch}
                onChange={(event) => setAddJobSearch(event.target.value)}
              />
            </div>

            <div className="cv-modal-list">
              {filteredModalJobs.length ? filteredModalJobs.map((job) => (
                <div
                  key={job.id}
                  className={`cv-modal-job-item ${selectedTargetJob === job.id ? 'selected' : ''}`}
                  onClick={() => setSelectedTargetJob(job.id)}
                >
                  <div className={`cv-modal-job-icon ${job.type === 'custom_list' ? 'custom-list' : ''}`}>
                    <i className={`fa-solid ${job.type === 'custom_list' ? 'fa-list' : 'fa-briefcase'}`} />
                  </div>
                  <div>
                    <div className="cv-modal-job-title">{job.title}</div>
                    <div className="cv-modal-job-type">{job.type === 'custom_list' ? t('imported.customList', 'Custom list') : t('imported.job', 'Job')}</div>
                  </div>
                </div>
              )) : <div className="cv-modal-empty">{t('imported.noJobs', 'No matching jobs found')}</div>}
            </div>

            <div className="cv-modal-footer">
              <button type="button" className="cv-modal-cancel" onClick={closeAddToJobModal}>
                {t('imported.cancel', 'Cancel')}
              </button>
              <button type="button" className="cv-modal-confirm" disabled={!selectedTargetJob || addingToJob} onClick={confirmAddToJob}>
                {addingToJob ? t('imported.adding', 'Adding...') : t('imported.addJob', 'Add to job')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`cv-toast ${toast.type}`} key={toast.key}>
          <i className={`fa-solid ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'error' ? 'fa-circle-exclamation' : 'fa-circle-info'}`} />
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default ImportedCVs;
